import type { Configuration, IPublicClientApplication, RedirectRequest } from "@azure/msal-browser";

const tenantId = "9786b406-6793-43d4-9478-c98385c12728";

export const msalConfig: Configuration = {
  auth: {
    clientId: "c4ff44fa-b865-4506-96a8-5ef39dad3d54",
    authority: `https://justscriptexternal.ciamlogin.com/${tenantId}`,
    redirectUri: "/",
    postLogoutRedirectUri: "/",
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: true,
  },
};

export const loginRequest: RedirectRequest = {
  scopes: ["openid", "profile", "offline_access"],
};

export const apiRequest = {
  scopes: ["api://65cd5792-ac33-4d56-b051-f05d5fdba7f2/API.Access"],
};

/**
 * True when the app is embedded in an iframe (e.g. the Shopify admin).
 * Cross-origin access to `window.top` throws — that also means we are framed.
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/**
 * Sign in. In an iframe (Shopify admin) third-party redirects are blocked, so
 * use a popup; otherwise redirect to `target` after login.
 * Returns true when login completed in-page (popup) and the caller should navigate.
 */
export async function signIn(instance: IPublicClientApplication, target: string): Promise<boolean> {
  if (isInIframe()) {
    const result = await instance.loginPopup(loginRequest);
    // The caller navigates straight away, before AuthSync reacts - make the account active now.
    instance.setActiveAccount(result.account);
    return true;
  }
  await instance.loginRedirect({
    ...loginRequest,
    redirectStartPage: new URL(target, window.location.origin).toString(),
  });
  return false;
}

/** Sign out, using a popup when framed (Shopify admin) and a redirect otherwise. */
export async function signOut(instance: IPublicClientApplication): Promise<void> {
  const account = instance.getActiveAccount();
  if (!account) throw new Error("No active account to sign out");

  // `login_hint` is an optional ID-token claim, and the claims themselves are absent while the
  // ID token is not in the cache. Without it Entra shows its account picker on sign-out.
  const logoutHint = account.idTokenClaims?.login_hint;

  if (isInIframe()) {
    await instance.logoutPopup({ account, logoutHint });
  } else {
    await instance.logoutRedirect({ account, logoutHint, postLogoutRedirectUri: "/" });
  }
}
